import * as Sentry from '@sentry/react'

import { AlertCircle, RefreshCw } from 'lucide-react'
import React, { useCallback, useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'

interface ErrorPageProps {
  error?: Error
  resetError?: () => void
}

const ErrorPage: React.FC<ErrorPageProps> = ({ error, resetError }) => {
  const [timestamp] = useState(new Date().toISOString())

  const handleRefresh = useCallback(() => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }, [resetError])

  const handleReportError = useCallback(() => {
    Sentry.showReportDialog({
      eventId: Sentry.lastEventId(),
    })
  }, [])

  return (
    <div className="absolute top-0 left-0 w-full h-full bg-gray-50">
      <div className="container max-w-2xl pt-24 pb-8 mx-auto">
        <Card>
          <CardHeader className="text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground">
              We apologize for the inconvenience. Our team has been notified.
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive">
                <AlertTitle>Error Details</AlertTitle>
                <AlertDescription className="break-words">{error.message}</AlertDescription>
              </Alert>
            )}

            <p className="pb-6 font-mono text-sm text-center text-muted-foreground">Error occurred at {timestamp}</p>

            <div className="flex justify-center gap-4">
              <Button onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button variant="outline" onClick={handleReportError}>
                Report Error
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ErrorPage
